import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import WagerPanel from "@/components/WagerPanel";
import { toast } from "sonner";
import { maybeCelebrate } from "@/lib/celebrate";
import { RotateCcw, ShieldCheck } from "lucide-react";
import { ChickenSvg, CarSvg, LaneStripes, AsphaltBg, CAR_PALETTES } from "@/pages/games/ChickenAssets";

const RTP = 0.30;
const stepMult = (p, step) => (1 / p) ** step * RTP;

const DIFFICULTY_LABEL = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  extreme: "Extreme",
};

export default function Chicken() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [difficulty, setDifficulty] = useState("medium");
  const [autoMult, setAutoMult] = useState(0);
  const [autoLane, setAutoLane] = useState(0);
  const [clientSeed, setClientSeed] = useState("");
  const [session, setSession] = useState(null);
  const [seedHash, setSeedHash] = useState("");
  const [seedReveal, setSeedReveal] = useState("");
  const [step, setStep] = useState(0);
  const [hit, setHit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [carAt, setCarAt] = useState(null);
  const settleGuard = useRef(false);

  useEffect(() => {
    api.get("/games/chicken/config").then((r) => setConfigs(r.data.difficulties));
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
  }, []);

  const cfg = configs.find((c) => c.id === difficulty);
  const p = cfg?.p ?? 0.85;
  const lanes = cfg?.lanes ?? 20;
  const currentMult = stepMult(p, step);
  const nextMult = stepMult(p, step + 1);
  const potential = session ? Math.floor(session.wager * currentMult) : 0;
  const nextPotential = session ? Math.floor(session.wager * nextMult) : 0;

  const settle = useCallback(async (cashedOut, stepsTaken, wasHit = false) => {
    if (settleGuard.current) return;
    settleGuard.current = true;
    setBusy(true);
    try {
      const { data } = await api.post("/games/settle", {
        session_id: session.session_id,
        outcome: { steps: stepsTaken, difficulty, cashed_out: cashedOut && !wasHit, hit: !!wasHit },
      });
      setResult(data);
      setSeedReveal(data.server_seed_reveal || "");
      if (data.payout > 0) {
        toast.success(`+${data.payout.toLocaleString()} chips`);
        maybeCelebrate({ payout: data.payout, wager: session.wager });
      } else toast.error(`No payout · lost ${session.wager}`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Settle failed"); }
    finally { setBusy(false); }
  }, [session, difficulty, refresh]);

  const start = async (wager) => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/wager", {
        game: "chicken",
        wager,
        config: { difficulty, client_seed: clientSeed, auto_mult: autoMult, auto_lane: autoLane },
      });
      settleGuard.current = false;
      setSession(data);
      setSeedHash(data.server_seed_hash || "");
      setSeedReveal("");
      setStep(0); setHit(false); setResult(null); setCarAt(null);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot start"); }
    finally { setBusy(false); }
  };

  const takeStep = async () => {
    if (!session || result || hit || animating || busy) return;
    setAnimating(true);
    setCarAt(step);
    try {
      const { data } = await api.post("/games/chicken/step", { session_id: session.session_id });
      await new Promise((r) => setTimeout(r, 480));
      if (data.safe) {
        setStep(data.step);
        setCarAt(null);
        setAnimating(false);
        const mult = data.mult;
        if (data.reached_end) {
          settle(true, data.step);
        } else if (autoMult > 0 && mult >= autoMult) {
          setTimeout(() => settle(true, data.step), 200);
        } else if (autoLane > 0 && data.step >= autoLane) {
          setTimeout(() => settle(true, data.step), 200);
        }
      } else {
        setHit(true);
        setCarAt(step);
        setAnimating(false);
        setTimeout(() => settle(false, step, true), 400);
      }
    } catch (e) {
      setAnimating(false); setCarAt(null);
      toast.error(e.response?.data?.detail || "Step failed");
    }
  };

  const cashout = () => settle(true, step);
  const restart = () => {
    setSession(null); setResult(null); setStep(0); setHit(false); setCarAt(null);
    setSeedHash(""); setSeedReveal("");
    setClientSeed(Math.random().toString(36).slice(2, 12).toUpperCase());
  };

  const copyRoundData = () => {
    navigator.clipboard.writeText(JSON.stringify({ server_seed_hash: seedHash, server_seed: seedReveal, client_seed: clientSeed }, null, 2));
    toast.success("Round proof copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="chicken-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-2">
            <span style={{ display: "inline-flex" }}><ChickenSvg size={16} /></span> Chicken Road
          </div>
          <h1 className="font-display text-3xl mt-2">Cross · cash · repeat</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/play/chicken/info" className="btn-ghost text-sm" data-testid="chicken-info-link">Fairness &amp; leaderboard</Link>
          <button onClick={() => nav("/lobby")} className="btn-ghost text-sm" data-testid="back-to-lobby">Lobby</button>
        </div>
      </div>

      {!session ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="vexis-card p-5">
            <div className="pill">Difficulty</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {configs.map((c) => (
                <button key={c.id} onClick={() => setDifficulty(c.id)} data-testid={`chicken-diff-${c.id}`}
                  className={`text-left p-3 rounded-xl border transition ${difficulty === c.id ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.06)]" : "border-white/10 text-slate-300 hover:border-white/20"}`}>
                  <div className="text-sm">{DIFFICULTY_LABEL[c.id]}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">1st step {c.first_step_mult.toFixed(2)}× · {c.lanes} lanes</div>
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <WagerPanel game="Chicken Road" onStart={start} busy={busy} />
            <div className="vexis-card p-4">
              <div className="pill">Auto-cashout</div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500">At multiplier ≥</label>
                  <input type="number" step="0.1" min="0" max="1000" value={autoMult} onChange={(e) => setAutoMult(parseFloat(e.target.value || "0"))} className="vexis mt-1" data-testid="auto-mult" placeholder="0 = off" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-slate-500">At lane ≥</label>
                  <input type="number" min="0" max={lanes} value={autoLane} onChange={(e) => setAutoLane(parseInt(e.target.value || "0"))} className="vexis mt-1" data-testid="auto-lane" placeholder="0 = off" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
          <div className={`vexis-card p-4 sm:p-6 select-none relative overflow-hidden ${result ? (result.payout > 0 ? "win-glow" : "loss-glow") : ""}`}>
            <div className="flex items-center justify-between mb-3 text-xs font-mono text-slate-500 uppercase tracking-widest">
              <span>Start</span>
              <span>Step {step} / {lanes}</span>
              <span>Finish</span>
            </div>
            <div className="relative rounded-2xl overflow-hidden" style={{background: "linear-gradient(180deg,#1B1F2A 0%,#0F131B 50%,#0A0D13 100%)"}}>
              <AsphaltBg />
              <div className="grid relative" style={{ gridTemplateColumns: `54px repeat(${lanes}, minmax(30px, 1fr)) 54px`, minHeight: 220 }}>
                <div className="flex items-center justify-center relative" style={{background: "repeating-linear-gradient(45deg,#1F2937 0 10px,#141A24 10px 20px)"}}>
                  <div className="absolute inset-y-0 right-0 w-[3px] bg-[color:var(--gold)]/40" />
                  {step === 0 && !hit && !result && (
                    <span style={{ display: "inline-block", animation: "chickBob 1.4s ease-in-out infinite" }} data-testid="chicken-avatar-start">
                      <ChickenSvg size={36} />
                    </span>
                  )}
                </div>
                {Array.from({ length: lanes }).map((_, i) => {
                  const isCurrentChicken = !hit && !result && i + 1 === step;
                  const isCarLane = carAt === i;
                  const passed = i < step;
                  return (
                    <div key={i} className={`relative border-l border-white/[0.04] ${passed ? "bg-[rgba(16,185,129,0.05)]" : ""}`} data-testid={`lane-${i}`}>
                      <LaneStripes />
                      {isCarLane && (
                        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 0, animation: "carPass .48s ease-out forwards" }}>
                          <CarSvg width={22} palette={CAR_PALETTES[i % CAR_PALETTES.length]} />
                        </div>
                      )}
                      {isCurrentChicken && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span style={{ display: "inline-block", animation: "chickBob 1.4s ease-in-out infinite" }} data-testid="chicken-avatar">
                            <ChickenSvg size={30} />
                          </span>
                        </div>
                      )}
                      {passed && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-mono text-emerald-400/70">
                          {stepMult(p, i + 1).toFixed(2)}×
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-center relative" style={{background: "repeating-linear-gradient(45deg,#1F2937 0 10px,#141A24 10px 20px)"}}>
                  <div className="absolute inset-y-0 left-0 w-[3px] bg-emerald-400/50" />
                  {step >= lanes && !hit && (
                    <span data-testid="chicken-finish"><ChickenSvg size={36} /></span>
                  )}
                </div>
              </div>
              <style>{`
                @keyframes carPass { 0% { transform: translate(-50%, -140%); } 100% { transform: translate(-50%, 260%); } }
                @keyframes chickBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
              `}</style>
            </div>
          </div>

          <div className="space-y-3">
            <div className="vexis-card p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-slate-500 text-xs font-mono uppercase">Current</div><div className="font-display text-xl text-[color:var(--gold)]" data-testid="chicken-mult">{currentMult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Potential</div><div className="font-display text-xl" data-testid="chicken-potential">{potential.toLocaleString()}</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Next step</div><div className="font-display text-xl">{nextMult.toFixed(2)}×</div></div>
                <div><div className="text-slate-500 text-xs font-mono uppercase">Wager</div><div className="font-display text-xl">{session.wager}</div></div>
              </div>
            </div>

            {!result && (
              <>
                <button onClick={takeStep} disabled={hit || animating || busy || step >= lanes} className="btn-gold w-full inline-flex justify-center items-center gap-2" data-testid="chicken-step">
                  <span style={{ display: "inline-flex" }}><ChickenSvg size={18} /></span> {animating ? "Crossing…" : `Cross → ${nextPotential.toLocaleString()} chips`}
                </button>
                <button onClick={cashout} disabled={step === 0 || hit || animating || busy} className="btn-ghost w-full" data-testid="chicken-cashout">
                  Cash out · {potential.toLocaleString()} chips
                </button>
              </>
            )}

            {result && (
              <div className={`vexis-card p-4 ${result.payout > 0 ? "win-glow" : "loss-glow"}`} data-testid="chicken-result">
                {result.payout >= session.wager * 2 && (
                  <div className="pill pill-gold mb-2 inline-flex" data-testid="chicken-mega">✦ Mega Win · {(result.payout / session.wager).toFixed(2)}×</div>
                )}
                <div className={`font-display text-2xl ${result.payout > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {result.payout > 0 ? "Cashed out" : hit ? "Splat" : "No payout"}
                </div>
                <div className="text-sm mt-1 text-slate-300">Payout · <span className="font-mono">{result.payout}</span> chips</div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <button onClick={copyRoundData} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="chicken-verify">
                    <ShieldCheck className="w-3 h-3" /> Copy proof
                  </button>
                  <Link to="/play/chicken/info" className="text-xs text-slate-400 hover:text-white">How to verify →</Link>
                </div>
                <button onClick={restart} className="btn-gold w-full mt-4 inline-flex justify-center items-center gap-2" data-testid="chicken-play-again"><RotateCcw className="w-4 h-4" /> Play again</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
